import React, { useState, useEffect, useRef } from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    FormGroup,
    Label,
    Input,
    Alert,
    Table,
    Badge,
    Spinner,
    Container,
    Modal,
    ModalHeader,
    ModalBody
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaMoneyBillWave, FaPlus, FaMinus, FaList, FaUser, FaCalendarAlt, FaEdit, FaTrash, FaShieldAlt, FaArrowUp, FaArrowDown, FaBalanceScale, FaArrowLeft, FaSearch } from 'react-icons/fa';
import { reglementActions } from '../../sagas/reglementSlice';
import { formatPrice, preventScroll, roundToNearestInteger } from '../../helpers/utility';
import Select from 'react-select';
import DocumentSelectionModal from './DocumentSelectionModal';
import './ReglementForm.css'; // Assuming you have some custom styles
import { clientActions } from '../../sagas/clientSlice';
import { deliveryActions } from '../../sagas/deliveryNoteSlice';
import { invoiceActions } from '../../sagas/invoiceSlice';
import { companyActions } from '../../sagas/companySlice';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/api_helper';
import { GuidedPaymentStepper, PaymentFinancialSummary, PaymentHelp } from '../Common/GuidedPaymentUX';
import '../Common/GuidedPaymentUX.css';

const ReglementForm = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { invoices: facturesImpayees, loading: loadingInvoices } = useSelector((state) => state.invoices);
    const { list: bonLivraisonsNonRegles, loading: loadingDeliveryNotes } = useSelector((state) => state.deliveryNotes);

    const { clients } = useSelector(state => state.clients);
    const { company: defaultCompany, role } = useSelector((state) => state.auth);
    const { companies } = useSelector((state) => state.companies);

    const navigate = useNavigate();
    const location = useLocation();
    const routeReglement = location.state?.reglement || null;
    const isAllocationMode = Boolean(location.state?.allocationMode);
    const [detailedReglement, setDetailedReglement] = useState(routeReglement);
    const reglement = detailedReglement || routeReglement;
    const quickPayment = location.state?.quickPayment || null;
    const quickDocuments = useMemo(
        () => (Array.isArray(quickPayment?.documents) ? quickPayment.documents : []),
        [quickPayment?.documents]
    );

    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedChantier, setSelectedChantier] = useState(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [typeReglement, setTypeReglement] = useState(quickPayment?.typeReglement || 'AVANCE');
    const [paymentPurpose, setPaymentPurpose] = useState(
        quickPayment?.typeReglement === 'AVANCE' ? 'ADVANCE' : 'DOCUMENTS'
    );
    const [isCautionPayment, setIsCautionPayment] = useState((reglement?.typeReglement || quickPayment?.typeReglement) === 'CAUTION');
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [showAvanceModal, setShowAvanceModal] = useState(false);
    const [avanceDocFilter, setAvanceDocFilter] = useState("NONE");
    const [availableAdvances, setAvailableAdvances] = useState([]);
	    const [selectedAdvanceIds, setSelectedAdvanceIds] = useState([]);
	    const advancePreferenceInitializedRef = useRef(false);
    const [loadingAdvances, setLoadingAdvances] = useState(false);
    const [documentFilters, setDocumentFilters] = useState({ query: '', amount: '' });
    const [documentSort, setDocumentSort] = useState({ key: 'date', direction: 'desc' });
    const [documentsInitialized, setDocumentsInitialized] = useState(false);
    const requestedInitialStep = Number(location.state?.initialStep || 1);
    const [activeStep, setActiveStep] = useState(Math.min(4, Math.max(1, requestedInitialStep)));
    const isDocumentBasedReglement = selectedDocuments.length > 0 || typeReglement === 'FACTURE' || typeReglement === 'BON_LIVRAISON';


    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglements);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);
    const formikRef = useRef(null);
    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });
    const isSuperAdmin = role === 'ROLE_SUPER_ADMIN';
    const hasAffectation = Boolean(reglement && (
        (Array.isArray(reglement.allocations) && reglement.allocations.length > 0) ||
        Number(reglement.totalDocumentCoverage || 0) > 0 ||
        Number(reglement.montantAffecte || 0) > 0 ||
        Number(reglement.externalAdvanceApplied || reglement.montantAvancesUtilisees || 0) > 0
    ));
    const canEdit = isAllocationMode || !reglement || (reglement.statut !== 'ANNULE' && !hasAffectation);
    const isQuickPayment = Boolean(quickPayment && !reglement);
    const isCompanyFieldDisabled = !canEdit || isDocumentBasedReglement;
    const resolvedDefaultCompanyId =
        reglement?.companyId ||
        reglement?.company?.id ||
        quickPayment?.companyId ||
        defaultCompany?.id ||
        companies?.[0]?.id ||
        null;

    useEffect(() => {
        if (!routeReglement?.id) return;
        let active = true;
        axiosInstance.get(`/api/reglements/${routeReglement.id}`)
            .then(({ data }) => {
                if (active && data) {
                    setDetailedReglement(data);
                    setDocumentsInitialized(false);
                }
            })
            .catch(() => {
                if (active) setDetailedReglement(routeReglement);
            });
        return () => {
            active = false;
        };
    }, [routeReglement]);

    // Validation schema
    const validationSchema = Yup.object().shape({
        companyId: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .required(t('company.companyRequired', 'Entreprise requise')),
        clientId: Yup.number().required(t('reglement.clientRequired')),
        // montantRegle: Yup.number()
        //     .min(0, t('reglement.montantPositive'))
        //     .required(t('reglement.montantRequired')),
        dateReglement: Yup.date().required(t('reglement.dateReglementRequired')),
        modeReglement: Yup.string().required(t('reglement.modeReglementRequired')),
        referencePaiement: Yup.string().when('modeReglement', {
            is: mode => ['VIREMENT', 'CARTE_BANCAIRE'].includes(mode),
            then: schema => schema.trim().required('La référence du paiement est obligatoire pour ce mode.'),
        }),
        numeroCheque: Yup.string().when('modeReglement', {
            is: mode => ['CHEQUE', 'TRAITE'].includes(mode),
            then: schema => schema.trim().required('Le numéro du chèque ou de la traite est obligatoire.'),
        }),
        statut: Yup.string().required(t('reglement.statutRequired')),

    });

    const extractDocumentNumeros = (reglement) => {
        if (!reglement) return [];

        const source =
            reglement.typeReglement === 'FACTURE'
                ? reglement.numFactures
                : reglement.numBonLivraisons;

        if (!source) return [];

        return source
            .split(',')
            .map(n => n.trim())
            .filter(Boolean);
    };

    const normalizeNumero = (value) => value?.toString().trim().toUpperCase() || '';

    const isAllocationOwnedByCurrentOperation = (allocation) => {
        if (!reglement?.id) return true;
        const operationId = allocation?.reglementConsommateurId ?? allocation?.reglementId;
        return Number(operationId) === Number(reglement.id);
    };

    const buildSelectedDocumentsFromAllocations = (allocations = []) => {
        if (!Array.isArray(allocations) || allocations.length === 0) {
            return [];
        }

        const groupedAllocations = allocations
            .filter(allocation => isAllocationOwnedByCurrentOperation(allocation) && normalizeNumero(allocation?.numeroDocument))
            .reduce((groups, allocation) => {
                const key = `${allocation.typeDocument || ''}-${allocation.documentId || normalizeNumero(allocation.numeroDocument)}`;
                const existing = groups.get(key);
                if (existing) {
                    existing.montantAlloue = Number(existing.montantAlloue || 0) + Number(allocation.montantAlloue || 0);
                } else {
                    groups.set(key, { ...allocation });
                }
                return groups;
            }, new Map());

        return Array.from(groupedAllocations.values())
            .map((allocation, index) => {
                const montantTotal = Number(allocation?.montantDocument || 0);
                const montantRestant = Number(allocation?.montantRestant || 0);
                const montantPaye = Math.max(
                    montantTotal - montantRestant,
                    Number(allocation?.montantAlloue || 0)
                );

                return {
                    id: allocation?.documentId || allocation?.id || `${allocation?.numeroDocument}-${index}`,
                    numero: allocation?.numeroDocument,
                    montantTotalTTC: montantTotal,
                    montantRegle: montantPaye,
                    montantPaye,
                    montantAAffecter: Number(allocation?.montantAlloue || 0),
                    paymentDocumentType: allocation?.typeDocument,
                    createdAt: allocation?.dateDocument || allocation?.dateOperation || allocation?.createdAt || null,
                    dateFacture: allocation?.dateDocument || null,
                    status: allocation?.statutDocument || allocation?.statut || null,
                    statut: allocation?.statutDocument || allocation?.statut || null,
                    clientNom: reglement?.nomClient || '',
                };
            });
    };


    // Initial values
    const initialValues = {
        id: reglement?.id || null,
        reference: reglement?.reference || '',
        companyId: resolvedDefaultCompanyId,
        clientId: reglement?.clientId || quickPayment?.clientId || '',
        numFactures: reglement?.numFactures || (quickPayment?.typeReglement === 'FACTURE' ? quickDocuments.map(doc => doc.numero).join(',') : ''),
        numBonLivraisons: reglement?.numBonLivraisons || (quickPayment?.typeReglement === 'BON_LIVRAISON' ? quickDocuments.map(doc => doc.numero).join(',') : ''),
        typeReglement: reglement?.typeReglement || quickPayment?.typeReglement || 'FACTURE',
        montantRegle: reglement?.montantRegle || 0,
        montantAffecte: reglement?.montantAffecte || 0,
        dateReglement: reglement?.dateReglement || new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        dateEncaissement: reglement?.dateEncaissement || '',
        statut: reglement?.statut || 'EN_ATTENTE',
        statutPrecedent: reglement?.statutPrecedent || null,
        commentaire: reglement?.commentaire || '',
        modeReglement: reglement?.modeReglement || 'ESPECES',
        referencePaiement: reglement?.referencePaiement || '',
        numeroCheque: reglement?.numeroCheque || '',
        banque: reglement?.banque || '',
        dateEcheance: reglement?.dateEcheance || '',
        motifAvance: reglement?.motifAvance || '',
        dateValiditeAvance: reglement?.dateValiditeAvance || '',
        commentaireAvance: reglement?.commentaireAvance || '',
    };

    const modeReglementOptions = [
        { value: 'ESPECES', label: t('reglement.especes') },
        { value: 'CHEQUE', label: t('reglement.cheque') },
        { value: 'TRAITE', label: t('reglement.traite') },
        { value: 'VIREMENT', label: t('reglement.virement') },
        { value: 'CARTE_BANCAIRE', label: t('reglement.carteBancaire') },
    ];

    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers
    })) ?? [];

    useEffect(() => {
        if (!selectedClient?.value || !Array.isArray(clients)) return;
        const freshClient = clients.find(client => Number(client.id) === Number(selectedClient.value));
        if (!freshClient) return;
        setSelectedClient(previous => ({
            ...previous,
            solde: freshClient.solde,
            totalDebit: freshClient.totalDebit,
            totalCredit: freshClient.totalCredit,
            chantiers: freshClient.chantiers,
        }));
    }, [clients, selectedClient?.value]);

    // Create chantier options from selected client's chantiers
    const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
        value: chantier.id,
        label: `${chantier.code} - ${chantier.nom}`,
        ...chantier
    })) || [];

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    const getDocumentTotalAmount = (document) =>
        Number(document?.montantTotalTTC ?? document?.montantTTC ?? 0);

    const getDocumentPaidAmount = (document) =>
        Number(document?.montantRegle ?? document?.montantPaye ?? 0);

    const getDocumentReturnAmount = (document) => Number(document?.montantRetourne ?? document?.montantAvoir ?? 0);
    const getDocumentNetAmount = (document) => Number(document?.montantNet ?? getDocumentTotalAmount(document));

    const getCurrentAllocation = (document) => Number(
        (Array.isArray(reglement?.allocations) ? reglement.allocations : [])
            .filter(allocation =>
                isAllocationOwnedByCurrentOperation(allocation) &&
                Number(allocation.documentId) === Number(document?.id) &&
                allocation.typeDocument === document?.paymentDocumentType
            )
            .reduce((total, allocation) => total + Number(allocation?.montantAlloue || 0), 0)
    );

    const getDocumentRemainingAmount = (document) => {
        const backendRemaining = document?.montantRestantGlobal ?? document?.montantRestant;
        const calculatedRemaining = backendRemaining !== undefined && backendRemaining !== null
            ? Number(backendRemaining)
            : Number(document?.montantNet ?? getDocumentTotalAmount(document)) - getDocumentPaidAmount(document);
        return Math.max(0, roundToNearestInteger(calculatedRemaining + getCurrentAllocation(document)));
    };

    const calculateDocumentsAmount = (documents = []) =>
        roundToNearestInteger(documents.reduce((total, doc) => total + Number(doc.montantAAffecter || 0), 0));

    const externalAdvances = useMemo(
        () => availableAdvances.filter(avance => Number(avance.reglementId) !== Number(reglement?.id)),
        [availableAdvances, reglement?.id]
    );

    const totalAvailableAdvances = useMemo(
        () => externalAdvances.reduce((total, avance) => total + Number(avance.montantDisponible || 0), 0),
        [externalAdvances]
    );

    const buildSelectedAdvanceUsages = (documents = selectedDocuments) => {
        let remaining = calculateDocumentsAmount(documents);
        if (remaining <= 0 || !externalAdvances.length || !selectedAdvanceIds.length) {
            return [];
        }

        return externalAdvances.reduce((usages, avance) => {
            if (remaining <= 0) return usages;
            if (!selectedAdvanceIds.includes(avance.id)) return usages;
            const disponible = Number(avance.montantDisponible || 0);
            const montantUtilise = Math.min(disponible, remaining);
            if (montantUtilise > 0) {
                usages.push({
                    avanceId: avance.id,
                    montantUtilise,
                    commentaire: `Utilisation avance ${avance.referenceReglement || avance.reglementId || avance.id}`
                });
                remaining -= montantUtilise;
            }
            return usages;
        }, []);
    };

    const calculateAdvanceUsageAmount = (documents = selectedDocuments) =>
        buildSelectedAdvanceUsages(documents).reduce((total, usage) => total + Number(usage.montantUtilise || 0), 0);

    const calculateSelectedAdvanceCapacity = () => externalAdvances.reduce(
        (total, avance) => selectedAdvanceIds.includes(avance.id)
            ? total + Math.max(0, Number(avance.montantDisponible || 0))
            : total,
        0
    );

    const applyAvailableAdvancesFirst = (setFieldValue) => {
        let remaining = calculateDocumentsAmount(selectedDocuments);
        const advanceIds = [];

        externalAdvances.forEach(advance => {
            if (remaining <= 0) return;
            const available = Math.max(0, Number(advance.montantDisponible || 0));
            if (available <= 0) return;
            advanceIds.push(advance.id);
            remaining = Math.max(0, remaining - available);
        });

        setSelectedAdvanceIds(advanceIds);
        setFieldValue('montantRegle', roundToNearestInteger(remaining));
    };

    const calculateCurrentPaymentAllocation = (documents = selectedDocuments) =>
        Math.max(0, calculateDocumentsAmount(documents) - calculateAdvanceUsageAmount(documents));

    const allocateDocumentsAutomatically = (documents, paymentAmount) => {
        let remainingBudget = Math.max(0, Number(paymentAmount || 0));
        return documents.map(document => {
            const documentBalance = getDocumentRemainingAmount(document);
            const allocated = Math.min(documentBalance, remainingBudget);
            remainingBudget = Math.max(0, remainingBudget - allocated);
            return { ...document, montantAAffecter: roundToNearestInteger(allocated) };
        });
    };

    const syncMontantRegleFromDocuments = (documents, setFieldValue) => {
        if (reglement) return;
        const amount = documents.reduce((total, document) => total + getDocumentRemainingAmount(document), 0);
        (setFieldValue || formikRef.current?.setFieldValue)?.('montantRegle', roundToNearestInteger(amount));
    };

    const applyExistingPaymentBudget = (documents) => reglement
        ? allocateDocumentsAutomatically(documents, formikRef.current?.values?.montantRegle ?? reglement.montantRegle)
        : documents;

    const goToStep = (step) => {
        setActiveStep(step);
        document.getElementById(`payment-step-${step}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const resolveDocumentCompanyId = (document) =>
        document?.companyId ||
        document?.company?.id ||
        document?.entrepriseId ||
        document?.societeId ||
        null;

    const resolveDocumentCompanyLabel = (document) =>
        document?.company?.name ||
        document?.companyName ||
        document?.entrepriseName ||
        document?.nomEntreprise ||
        '-';

    const resolveCompanyDisplayName = (companyId, documents = selectedDocuments) => {
        const normalizedCompanyId = companyId === null || companyId === undefined || companyId === ''
            ? null
            : Number(companyId);
        const matchingCompany = normalizedCompanyId === null
            ? null
            : companies?.find(company => Number(company.id) === normalizedCompanyId);

        if (matchingCompany?.name) {
            return matchingCompany.name;
        }

        const sourceDocuments = Array.isArray(documents) ? documents : [];
        const matchingDocument = normalizedCompanyId === null
            ? null
            : sourceDocuments.find(document => Number(resolveDocumentCompanyId(document)) === normalizedCompanyId);
        const sourceDocument = matchingDocument || (sourceDocuments.length === 1 ? sourceDocuments[0] : null);
        const documentCompanyLabel = sourceDocument ? resolveDocumentCompanyLabel(sourceDocument) : '-';

        if (documentCompanyLabel !== '-') {
            return documentCompanyLabel;
        }

        const reglementCompanyId = reglement?.companyId || reglement?.company?.id;
        if (normalizedCompanyId !== null && Number(reglementCompanyId) === normalizedCompanyId) {
            return reglement?.company?.name || reglement?.companyName || reglement?.nomEntreprise || '-';
        }

        if (normalizedCompanyId !== null && Number(quickPayment?.companyId) === normalizedCompanyId) {
            return quickPayment?.company?.name || quickPayment?.companyName || quickPayment?.nomEntreprise || '-';
        }

        if (normalizedCompanyId !== null && Number(defaultCompany?.id) === normalizedCompanyId) {
            return defaultCompany?.name || '-';
        }

        return '-';
    };

    const syncCompanyFromDocuments = (documents, setFieldValue = formikRef.current?.setFieldValue) => {
        if (!Array.isArray(documents) || documents.length === 0) {
            return;
        }

        const companyDocument = documents.find(document =>
            resolveDocumentCompanyId(document) || resolveDocumentCompanyLabel(document) !== '-'
        );
        const documentCompanyId = companyDocument ? resolveDocumentCompanyId(companyDocument) : null;
        const documentCompanyLabel = companyDocument ? resolveDocumentCompanyLabel(companyDocument) : '-';
        const companyMatchedByName = !documentCompanyId && documentCompanyLabel !== '-'
            ? companies?.find(company => company.name?.trim().toLocaleLowerCase() === documentCompanyLabel.trim().toLocaleLowerCase())
            : null;
        const nextCompanyId = documentCompanyId || companyMatchedByName?.id || null;

        if (!nextCompanyId) {
            return;
        }

        setSelectedCompanyId(nextCompanyId);
        if (setFieldValue) {
            setFieldValue('companyId', nextCompanyId);
        }
    };

    const compactSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '34px',
            fontSize: '0.875rem',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '34px',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 }),
    };

    useEffect(() => {
        dispatch(clientActions.search({
            query: ""
        }));

        if (!companies || companies.length === 0) {
            dispatch(companyActions.getAll({}));
        }
    }, [companies, dispatch]);

    useEffect(() => {
        setSelectedCompanyId(resolvedDefaultCompanyId);
    }, [resolvedDefaultCompanyId]);

    useEffect(() => {
        if (!isQuickPayment) {
            return;
        }

        setTypeReglement(quickPayment.typeReglement || 'FACTURE');
        setIsCautionPayment(quickPayment.typeReglement === 'CAUTION');
        setSelectedCompanyId(quickPayment.companyId || resolvedDefaultCompanyId);
        setSelectedDocuments([]);
        setDocumentsInitialized(false);
    }, [isQuickPayment, quickPayment, quickDocuments, resolvedDefaultCompanyId]);

    useEffect(() => {
        if (!isQuickPayment || !quickPayment?.clientId || !clients?.length) {
            return;
        }

        const client = clients.find(item => Number(item.id) === Number(quickPayment.clientId));
        if (!client) {
            return;
        }

        setSelectedClient({
            value: client.id,
            label: client.typeClient === 'PARTICULIER'
                ? `${client.nom || ''} ${client.prenom || ''}`.trim()
                : client.entreprise,
            solde: client.solde,
            totalDebit: client.totalDebit,
            totalCredit: client.totalCredit,
            chantiers: client.chantiers
        });

        if (quickPayment.chantierId && client.chantiers) {
            const chantier = client.chantiers.find(item => Number(item.id) === Number(quickPayment.chantierId));
            setSelectedChantier(chantier ? {
                value: chantier.id,
                label: `${chantier.code} - ${chantier.nom}`,
                ...chantier
            } : null);
        }

        setFilters(f => ({ ...f, clientId: client.id }));
        dispatch(reglementActions.echeanceClient({ clientId: client.id, statut: "EN_ATTENTE" }));
        dispatch(reglementActions.cautionClient({ clientId: client.id, statut: "EN_ATTENTE" }));
    }, [clients, dispatch, isQuickPayment, quickPayment?.chantierId, quickPayment?.clientId]);


    // Initialize form with existing reglement data
    useEffect(() => {
        if (reglement) {
            // Set selected client
            const client = clients.find(c => c.id === reglement.clientId);
            if (client) {
                setSelectedClient({
                    value: client.id,
                    label: client.typeClient === 'PARTICULIER'
                        ? `${client.prenom} ${client.nom}`
                        : client.entreprise,
                    solde: client.solde,
                    totalDebit: client.totalDebit,
                    totalCredit: client.totalCredit,
                    chantiers: client.chantiers
                });

                // Set selected chantier if exists
                if (reglement.chantierId && client.chantiers) {
                    const chantier = client.chantiers.find(ch => ch.id === reglement.chantierId);
                    if (chantier) {
                        setSelectedChantier({
                            value: chantier.id,
                            label: `${chantier.code} - ${chantier.nom}`,
                            ...chantier
                        });
                    }
                }

                dispatch(reglementActions.echeanceClient({ ...filters, clientId: client.id }));
                dispatch(reglementActions.cautionClient({ ...filters, clientId: client.id }));
            }

            setTypeReglement(reglement.typeReglement);
            setPaymentPurpose(reglement.typeReglement === 'AVANCE' ? 'ADVANCE' : 'DOCUMENTS');
            setIsCautionPayment(reglement.typeReglement === 'CAUTION');

        }
    }, [reglement, clients]);

    useEffect(() => {
        if (
            !reglement ||
            documentsInitialized ||
            !selectedClient
        ) return;

        const documentsFromAllocations = buildSelectedDocumentsFromAllocations(reglement.allocations);
        if (documentsFromAllocations.length > 0) {
            setSelectedDocuments(documentsFromAllocations);
            setDocumentsInitialized(true);
            return;
        }

        const sourceDocuments =
            typeReglement === 'FACTURE'
                ? facturesImpayees
                : bonLivraisonsNonRegles;

        if (!Array.isArray(sourceDocuments) || sourceDocuments.length === 0) {
            return;
        }

        const numeros = extractDocumentNumeros(reglement);

        if (numeros.length === 0) {
            setDocumentsInitialized(true);
            return;
        }

        const matchedDocuments = sourceDocuments.filter(doc =>
            numeros.some(numero => normalizeNumero(numero) === normalizeNumero(doc.numero))
        );
        if (matchedDocuments.length > 0) {
            setSelectedDocuments(matchedDocuments);
        }

        setDocumentsInitialized(true);
    }, [
        reglement,
        typeReglement,
        selectedClient,
        facturesImpayees,
        bonLivraisonsNonRegles,
        documentsInitialized
    ]);


    // Load documents when client changes
    useEffect(() => {
        if (selectedClient) {
            const companyIdFilter = selectedCompanyId ?? null;
            dispatch(invoiceActions.listWithSubInvoices({
                clientId: selectedClient.value,
                companyId: companyIdFilter,
            }));
            dispatch(deliveryActions.list({
                clientId: selectedClient.value,
                companyId: companyIdFilter,
            }));
        }
    }, [selectedClient, dispatch, clients, selectedCompanyId]);

    useEffect(() => {
        if (!selectedClient?.value || !selectedCompanyId) {
	        advancePreferenceInitializedRef.current = false;
            setAvailableAdvances([]);
            setSelectedAdvanceIds([]);
            setLoadingAdvances(false);
            return;
        }

        let cancelled = false;
	    advancePreferenceInitializedRef.current = true;
        setLoadingAdvances(true);
        setAvailableAdvances([]);
        setSelectedAdvanceIds([]);
        axiosInstance.get(`/api/avances/client/${selectedClient.value}/disponibles`, {
            params: { companyId: selectedCompanyId, currentReglementId: reglement?.id || undefined }
        })
            .then(({ data }) => {
                if (!cancelled) {
	                advancePreferenceInitializedRef.current = Boolean(reglement);
                    setAvailableAdvances(Array.isArray(data) ? data : []);
                    const existingUsages = Array.isArray(reglement?.utilisationAvances) ? reglement.utilisationAvances : [];
                    setSelectedAdvanceIds(existingUsages.map(usage => usage.avanceId));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAvailableAdvances([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingAdvances(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [selectedClient?.value, selectedCompanyId, reglement?.id, reglement?.utilisationAvances]);

	useEffect(() => {
	    if (
	        reglement || loadingAdvances || advancePreferenceInitializedRef.current ||
	        paymentPurpose !== 'DOCUMENTS' || selectedDocuments.length === 0 ||
	        externalAdvances.length === 0
	    ) return;

	    advancePreferenceInitializedRef.current = true;
	    applyAvailableAdvancesFirst(formikRef.current?.setFieldValue);
	}, [reglement, loadingAdvances, paymentPurpose, selectedDocuments, externalAdvances]);

    const toggleDocumentSelection = (document, isSelected, setFieldValue) => {
        if (isSelected) {
            setIsCautionPayment(false);
            setTypeReglement(document.paymentDocumentType || typeReglement);
        }
        setSelectedDocuments(prev => {
            let nextDocuments;

            if (isSelected) {
                if (prev.some(doc => doc.id === document.id)) {
                    nextDocuments = prev;
                } else {
                    nextDocuments = [...prev, { ...document, montantAAffecter: getDocumentRemainingAmount(document) }];
                }
            } else {
                nextDocuments = prev.filter(doc => doc.id !== document.id);
            }

            const allocatedDocuments = applyExistingPaymentBudget(nextDocuments);
            syncCompanyFromDocuments(allocatedDocuments, setFieldValue);
            syncMontantRegleFromDocuments(allocatedDocuments, setFieldValue);
            return allocatedDocuments;
        });
    };

    const handleDocumentsSelected = (documents, setFieldValue) => {
        if (documents.length > 0) {
            setIsCautionPayment(false);
            setTypeReglement(documents[0].paymentDocumentType || typeReglement);
        }
        const allocatedDocuments = documents.reduce((result, document) => {
            result.push({ ...document, montantAAffecter: getDocumentRemainingAmount(document) });
            return result;
        }, []);
        setSelectedDocuments(applyExistingPaymentBudget(allocatedDocuments));
        syncCompanyFromDocuments(documents, setFieldValue);
        syncMontantRegleFromDocuments(documents, setFieldValue);
    };

    // Calculate total amount
    const calculateTotalAmount = () => calculateDocumentsAmount(selectedDocuments);

    // Remove document from selection
    const removeDocument = (documentId, setFieldValue) => {
        setSelectedDocuments(prev => {
            const nextDocuments = prev.filter(doc => doc.id !== documentId);
            const allocatedDocuments = applyExistingPaymentBudget(nextDocuments);
            syncCompanyFromDocuments(allocatedDocuments, setFieldValue);
            syncMontantRegleFromDocuments(allocatedDocuments, setFieldValue);
            return allocatedDocuments;
        });
    };


    const getSelectedInvoices = () =>
        selectedDocuments.filter(doc => Number(doc.montantAAffecter || 0) > 0 && (doc.paymentDocumentType === 'FACTURE' || (typeReglement === 'FACTURE' && !doc.paymentDocumentType)));

    const getSelectedDeliveryNotes = () =>
        selectedDocuments.filter(doc => Number(doc.montantAAffecter || 0) > 0 && (doc.paymentDocumentType === 'BON_LIVRAISON' || (typeReglement === 'BON_LIVRAISON' && !doc.paymentDocumentType)));

    const resolveEffectiveTypeReglement = () => {
        if (isCautionPayment && selectedDocuments.length === 0) return 'CAUTION';
        if (paymentPurpose === 'ADVANCE') return 'AVANCE';
        if (selectedDocuments.length === 0) return 'AVANCE';
        return getSelectedInvoices().length > 0 ? 'FACTURE' : 'BON_LIVRAISON';
    };

    const getBusinessErrorMessage = (error) =>
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t('common.errorOccurred');

    const handleSubmit = async (values, { setSubmitting }) => {
        if (paymentPurpose === 'DOCUMENTS' && selectedDocuments.length === 0) {
            const choice = await Swal.fire({
                icon: 'question',
                title: 'Aucun document sélectionné',
                text: 'Vous pouvez revenir à la sélection ou enregistrer le montant comme avance.',
                showCancelButton: true,
                confirmButtonText: 'Enregistrer comme avance',
                cancelButtonText: 'Revenir à la sélection'
            });
            if (!choice.isConfirmed) {
                setActiveStep(2);
                setSubmitting(false);
                return;
            }
            setPaymentPurpose('ADVANCE');
            setTypeReglement('AVANCE');
        }
        if (
            paymentPurpose === 'DOCUMENTS' &&
            selectedDocuments.length > 0 &&
            totalAvailableAdvances > 0 &&
            selectedAdvanceIds.length === 0 &&
            Number(values.montantRegle || 0) > 0
        ) {
            const choice = await Swal.fire({
                icon: 'warning',
                title: 'Avances disponibles non utilisées',
                html: `<div class="text-start"><p>Ce client dispose de <b>${formatPrice(totalAvailableAdvances)}</b> d’avances pour cette entreprise.</p><p>Vous avez saisi un nouveau montant sans utiliser ces avances.</p></div>`,
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: 'Continuer avec le nouveau montant',
                denyButtonText: 'Utiliser les avances',
                cancelButtonText: 'Revenir au formulaire'
            });
            if (choice.isDenied) {
                applyAvailableAdvancesFirst(formikRef.current?.setFieldValue);
                setActiveStep(3);
                setSubmitting(false);
                return;
            }
            if (!choice.isConfirmed) {
                setActiveStep(3);
                setSubmitting(false);
                return;
            }
        }
        const effectiveTypeReglement = paymentPurpose === 'ADVANCE' || selectedDocuments.length === 0
            ? 'AVANCE' : resolveEffectiveTypeReglement();
        const selectedInvoices = getSelectedInvoices();
        const selectedDeliveryNotes = getSelectedDeliveryNotes();
        const automaticAdvanceUsages =
            effectiveTypeReglement === 'AVANCE' || effectiveTypeReglement === 'CAUTION'
                ? []
                : buildSelectedAdvanceUsages(selectedDocuments);
        const totalAvancesUtilisees = automaticAdvanceUsages.reduce((total, usage) => total + Number(usage.montantUtilise || 0), 0);
        const totalAllocated = calculateTotalAmount();
        const currentPaymentAllocated = Math.max(0, totalAllocated - totalAvancesUtilisees);
        const companyDisplayName = resolveCompanyDisplayName(values.companyId, selectedDocuments);
        const invalidDocument = selectedDocuments.find(document =>
            Number(document.montantAAffecter || 0) > getDocumentRemainingAmount(document)
        );
        if (invalidDocument) {
            Swal.fire({ icon: 'error', title: 'Répartition incorrecte', text: `L’affectation dépasse le solde du document ${invalidDocument.numero}.` });
            setActiveStep(3);
            setSubmitting(false);
            return;
        }
        if (currentPaymentAllocated > Number(values.montantRegle || 0)) {
            Swal.fire({ icon: 'error', title: t('common.error'), text: 'La part affectée depuis ce règlement dépasse le montant reçu.' });
            setActiveStep(3);
            setSubmitting(false);
            return;
        }
        if (!reglement) {
            const unapplied = Math.max(0, Number(values.montantRegle || 0) - currentPaymentAllocated);
            const confirmation = await Swal.fire({
                icon: 'question',
                title: effectiveTypeReglement === 'AVANCE' ? 'Confirmer l’avance client' : 'Confirmer le règlement client',
                html: `<div class="text-start"><p>Vérifiez les conséquences avant confirmation.</p><ul><li>Entreprise : <b>${companyDisplayName}</b></li><li>Montant reçu : <b>${formatPrice(values.montantRegle || 0)}</b></li><li>Affecté depuis ce règlement : <b>${formatPrice(currentPaymentAllocated)}</b></li><li>Avances externes utilisées : <b>${formatPrice(totalAvancesUtilisees)}</b></li><li>Total couvert : <b>${formatPrice(totalAllocated)}</b></li><li>Reste disponible sur ce règlement : <b>${formatPrice(unapplied)}</b></li></ul><p>Après confirmation, les soldes et statuts des documents sélectionnés seront recalculés.</p></div>`,
                showCancelButton: true,
                confirmButtonText: effectiveTypeReglement === 'AVANCE' ? 'Enregistrer l’avance' : 'Confirmer le règlement client',
                cancelButtonText: 'Revenir au formulaire',
            });
            if (!confirmation.isConfirmed) {
                setSubmitting(false);
                return;
            }
        }
        if (reglement) {
            const advanceAfter = Math.max(0, Number(values.montantRegle || 0) - currentPaymentAllocated);
            const confirmation = await Swal.fire({
                icon: 'info',
                title: 'Impact de la modification',
                html: `<div class="text-start"><p>Cette modification va recalculer les documents, soldes et mouvements concernés.</p><ul><li>Entreprise : <b>${companyDisplayName}</b></li><li>Montant : <b>${formatPrice(reglement.montantRegle || 0)}</b> vers <b>${formatPrice(values.montantRegle || 0)}</b></li><li>Affecté depuis ce règlement : <b>${formatPrice(currentPaymentAllocated)}</b></li><li>Avances externes utilisées : <b>${formatPrice(totalAvancesUtilisees)}</b></li><li>Total couvert : <b>${formatPrice(totalAllocated)}</b></li><li>Documents : <b>${selectedDocuments.length}</b></li><li>Reste disponible après modification : <b>${formatPrice(advanceAfter)}</b></li></ul></div>`,
                showCancelButton: true,
                confirmButtonText: 'Confirmer la modification',
                cancelButtonText: 'Continuer la modification'
            });
            if (!confirmation.isConfirmed) {
                setSubmitting(false);
                return;
            }
        }
        const isEcheancePayment = ['CHEQUE', 'TRAITE'].includes(values.modeReglement);
        const payload = {
            data: {
                ...values,
                numeroCheque: isEcheancePayment ? values.numeroCheque : null,
                banque: isEcheancePayment ? values.banque : null,
                dateEcheance: isEcheancePayment ? values.dateEcheance : null,
                companyId: values.companyId ? Number(values.companyId) : null,
                typeReglement: effectiveTypeReglement,
                chantierId: selectedChantier ? selectedChantier.value : null,
                numFactures: selectedInvoices.map(doc => doc.numero).filter(Boolean).join(','),
                numBonLivraisons: selectedDeliveryNotes.map(doc => doc.numero).filter(Boolean).join(','),
                montantRegle: roundToNearestInteger(values.montantRegle),
                montantAffecte: effectiveTypeReglement === 'AVANCE' || effectiveTypeReglement === 'CAUTION' ? 0 : roundToNearestInteger(totalAllocated),
                documentsIds: effectiveTypeReglement === 'AVANCE' || effectiveTypeReglement === 'CAUTION' ? [] : selectedDocuments.filter(doc => Number(doc.montantAAffecter || 0) > 0).map(doc => doc.id),
                estAvance: effectiveTypeReglement === 'AVANCE',

                utilisationAvances: automaticAdvanceUsages,
                montantAvancesUtilisees: totalAvancesUtilisees,
                montantReglementNet: values.montantRegle || 0,
                allocations: selectedDocuments.filter(document => Number(document.montantAAffecter || 0) > 0).map(document => ({
                    documentId: document.id,
                    typeDocument: document.paymentDocumentType,
                    numeroDocument: document.numero,
                    montantDocument: getDocumentTotalAmount(document),
                    montantAlloue: roundToNearestInteger(document.montantAAffecter),
                    montantRestant: Math.max(0, roundToNearestInteger(getDocumentRemainingAmount(document) - Number(document.montantAAffecter || 0)))
                })),
                // Champs spécifiques aux avances
                dateValiditeAvance: effectiveTypeReglement === 'AVANCE' ? values.dateValiditeAvance : null,
                commentaireAvance: effectiveTypeReglement === 'AVANCE' ? values.commentaireAvance : null,
            },
            onSuccess: (data) => {
                const unapplied = Math.max(0, Number(values.montantRegle || 0) - Math.max(0, totalAllocated - totalAvancesUtilisees));
                Swal.fire({
                    icon: 'success',
                    title: reglement ? 'Règlement mis à jour' : 'Règlement client enregistré',
                    html: `<div class="text-start"><p>Référence : <b>${data?.reference || values.reference || data?.id || '-'}</b></p><ul><li>Affecté depuis ce règlement : <b>${formatPrice(currentPaymentAllocated)}</b></li><li>Avances externes utilisées : <b>${formatPrice(totalAvancesUtilisees)}</b></li><li>Total couvert : <b>${formatPrice(totalAllocated)}</b></li><li>Reste disponible : <b>${formatPrice(unapplied)}</b></li><li>Documents mis à jour : <b>${selectedDocuments.length}</b></li></ul></div>`,
                });
                navigate("/sales-module/payments", {
                    state: { recentPaymentId: data?.id || reglement?.id || null }
                });
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('common.error'),
                    text: getBusinessErrorMessage(error),
                });
                setSubmitting(false);
            }
        };

        if (reglement) {
            const updateAction = reglement.statut === 'ENCAISSE'
                ? reglementActions.updateEncaisseRequest
                : reglementActions.updateRequest;

            dispatch(updateAction({ ...payload, id: reglement.id }));
        } else {
            dispatch(reglementActions.createRequest(payload));
        }
    };

    const availableDocuments = [
        ...facturesImpayees
            .filter(f => f.statut !== 'ANNULEE' && f.statut !== 'PAYEE')
            .map(doc => ({ ...doc, paymentDocumentType: 'FACTURE' })),
        ...bonLivraisonsNonRegles
            .map(doc => ({ ...doc, paymentDocumentType: 'BON_LIVRAISON' }))
            .filter(doc => getDocumentRemainingAmount(doc) > 0),
    ];

    const filteredAvailableDocuments = (Array.isArray(availableDocuments) ? availableDocuments : []).filter((document) => {
        const search = documentFilters.query.trim().toLowerCase();
        const hasAmountFilter = documentFilters.amount !== '' && documentFilters.amount !== null;
        const amount = Number(documentFilters.amount || 0);
        const remainingAmount = getDocumentRemainingAmount(document);

        const matchesSearch = !search || [
            document.numero,
            document.reference,
            document.clientNom,
            resolveDocumentCompanyLabel(document),
        ].some(value => value?.toString().toLowerCase().includes(search));

        const matchesAmount = !hasAmountFilter || Math.abs(remainingAmount - amount) < 0.0001;
        return matchesSearch && matchesAmount && remainingAmount > 0;
    });

    const getDocumentSortValue = (document, key) => {
        switch (key) {
            case 'numero':
                return document.numero || '';
            case 'date': {
                const date = document.dateOperation || document.dateFacture || document.createdAt;
                return date ? new Date(date).getTime() || 0 : 0;
            }
            case 'total':
                return getDocumentTotalAmount(document);
            case 'paid':
                return getDocumentPaidAmount(document);
            case 'remaining':
                return getDocumentRemainingAmount(document);
            case 'status':
                return document.statut || document.status || '';
            default:
                return '';
        }
    };

    const sortedAvailableDocuments = [...filteredAvailableDocuments].sort((a, b) => {
        const left = getDocumentSortValue(a, documentSort.key);
        const right = getDocumentSortValue(b, documentSort.key);

        if (typeof left === 'number' && typeof right === 'number') {
            return documentSort.direction === 'asc' ? left - right : right - left;
        }

        return documentSort.direction === 'asc'
            ? String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
            : String(right).localeCompare(String(left), undefined, { numeric: true, sensitivity: 'base' });
    });
    const selectableVisibleDocuments = sortedAvailableDocuments.filter(document => getDocumentRemainingAmount(document) > 0);
    const allVisibleDocumentsSelected = selectableVisibleDocuments.length > 0
        && selectableVisibleDocuments.every(document => selectedDocuments.some(selected => selected.id === document.id));

    const selectAllVisibleDocuments = () => {
        if (!canEdit) return;
        if (selectableVisibleDocuments.length === 0) return;
        setIsCautionPayment(false);
        setSelectedDocuments(previous => {
            const selectedIds = new Set(previous.map(document => document.id));
            return applyExistingPaymentBudget([
                ...previous,
                ...selectableVisibleDocuments
                    .filter(document => !selectedIds.has(document.id))
                    .map(document => ({ ...document, montantAAffecter: getDocumentRemainingAmount(document) })),
            ]);
        });
    };

    const handleDocumentSort = (key) => {
        setDocumentSort(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const renderDocumentStatusBadge = (document) => {
        const status = document.statut || document.status || 'INCONNU';
        const color = ['PAYEE', 'REGLE', 'LIVRE', 'VALIDEE'].includes(status)
            ? 'success'
            : ['PARTIELLEMENT_PAYEE', 'PARTIEL', 'EN_COURS'].includes(status)
                ? 'warning'
                : ['ANNULEE', 'ANNULE', 'RETOUR'].includes(status)
                    ? 'danger'
                    : 'secondary';
        return <Badge color={color}>{String(status).replace(/_/g, ' ')}</Badge>;
    };

    const hasInvoiceDocuments = filteredAvailableDocuments.some(document => document.paymentDocumentType === 'FACTURE');

	    const documentLoading = loadingInvoices || loadingDeliveryNotes;

    return (
        <Container fluid className="reglement-form payment-form-compact-page payment-form-modern">
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur }) => (
                    <Form className="payment-form-shell">
                        <div className="payment-form-header">
                            <div>
                                <div className="payment-form-eyebrow">Paiement client</div>
                                <h2>{isAllocationMode ? "Affecter l'avance" : reglement ? t('reglement.edit') : t('reglement.new')}</h2>
                                <p>{isAllocationMode
                                    ? "Sélectionnez les documents à régler avec le montant disponible de cette avance."
                                    : "Sélectionnez le client, consultez les impayés, puis saisissez le règlement ou une avance."}</p>
                            </div>
                            <div className="payment-header-actions">
                                <Badge color={reglement?.statut === 'ENCAISSE' ? 'success' : reglement?.statut === 'ANNULE' ? 'danger' : 'info'}>
                                    {reglement?.statut || 'NOUVEAU'}
                                </Badge>
                                <Button
                                    color="secondary"
                                    outline
                                    size="sm"
                                    onClick={() => navigate(-1)}
                                    className="d-flex align-items-center"
                                >
                                    <FaArrowLeft className="me-2" />
                                    {t('common.back', 'Retour')}
                                </Button>
                                {canEdit && (
                                    <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        className="payment-primary-action"
                                        onClick={() => goToStep(4)}
                                        disabled={isSubmitting || !selectedClient}
                                    >
                                        {isSubmitting && <Spinner size="sm" className="me-2" />}
                                        <i className={`fas ${reglement ? 'fa-save' : 'fa-plus'} me-1`}></i>
                                        Vérifier le règlement
                                    </Button>
                                )}
                            </div>
                        </div>
                        {reglement && (
                            <Alert color={canEdit ? "info" : "warning"} className="py-2 mb-2">
                                {canEdit
                                    ? "Vous pouvez modifier ce règlement tant qu'il n'est affecté à aucun document."
                                    : "Ce règlement a déjà été affecté. Il est consultable, mais ne peut plus être modifié."}
                            </Alert>
                        )}
                        <GuidedPaymentStepper activeStep={activeStep} onStepChange={goToStep} />
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Button type="button" color="secondary" outline size="sm" disabled={activeStep === 1} onClick={() => goToStep(paymentPurpose === 'ADVANCE' && activeStep === 3 ? 1 : activeStep - 1)}>
                                Étape précédente
                            </Button>
                            {activeStep < 4 && (
                                <Button type="button" color="primary" size="sm" onClick={() => goToStep(paymentPurpose === 'ADVANCE' && activeStep === 1 ? 3 : activeStep + 1)}>
                                    Continuer
                                </Button>
                            )}
                        </div>
                        <div className={`payment-workspace payment-workspace-step-${activeStep}`}>
                        <section className="payment-main-pane">
                        <Row className="payment-form-left-item payment-identity-shell g-0">
                            {/* Section Client et Type */}
                            <Col md={12}>
                                <Card id="payment-step-1" className={`payment-form-panel payment-form-identity-panel mb-3 ${activeStep === 1 ? '' : 'd-none'}`}>
                                    <CardHeader className="payment-form-section-header">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0">
                                                <FaMoneyBillWave className="me-2" />
                                                {t('reglement.informationsGenerales')}
                                            </h5>
                                        </div>
                                    </CardHeader>
                                    <CardBody className="py-3">
                                        <Row className="g-2 align-items-end">
                                            <Col md={12}>
                                                <FormGroup className="mb-2">
                                                    <Label className="fw-bold">Que souhaitez-vous faire ?</Label>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        <Button type="button" color={paymentPurpose === 'DOCUMENTS' ? 'primary' : 'light'} onClick={() => { setPaymentPurpose('DOCUMENTS'); setTypeReglement('BON_LIVRAISON'); }} disabled={!canEdit}>Régler des documents</Button>
                                                        <Button type="button" color={paymentPurpose === 'ADVANCE' ? 'primary' : 'light'} onClick={() => { setPaymentPurpose('ADVANCE'); setTypeReglement('AVANCE'); setSelectedDocuments([]); setIsCautionPayment(false); }} disabled={!canEdit}>Enregistrer une avance</Button>
                                                    </div>
                                                    <div className="payment-form-field-hint mt-1">Une avance utilise le paiement existant et reste disponible sans créer un second encaissement.</div>
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label for="clientId" className="fw-bold text-secondary small mb-1">
                                                        <FaUser className="me-1" />
                                                        {t('reglement.client')} *
                                                    </Label>
                                                    <Select
                                                        options={clientOptions}
                                                        value={selectedClient}
                                                        onChange={(option) => {
                                                            if (canEdit) {
                                                                setSelectedClient(option);
                                                                setSelectedChantier(null);
                                                                setFieldValue('clientId', option?.value || '');
                                                                setSelectedDocuments([]);
                                                                setIsCautionPayment(false);
                                                                setDocumentFilters({ query: '', amount: '' });
                                                                setDocumentsInitialized(false);
                                                                if (option) {
                                                                    dispatch(reglementActions.echeanceClient({ ...filters, clientId: option.value }));
                                                                    dispatch(reglementActions.cautionClient({ ...filters, clientId: option.value }));
                                                                    setFilters(f => ({ ...f, clientId: option.value }));
                                                                }
                                                            }
                                                        }}
                                                        placeholder={t('reglement.selectClient')}
                                                        isClearable={!reglement && !isQuickPayment}
                                                        isSearchable={!reglement && !isQuickPayment}
                                                        isDisabled={!canEdit || isQuickPayment}
                                                        className={`${errors.clientId && touched.clientId ? 'is-invalid' : ''}`}
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        styles={compactSelectStyles}
                                                    />
                                                    <ErrorMessage name="clientId" component="div" className="text-danger small mt-1" />
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label className="fw-bold text-secondary small mb-1">
                                                        {t('reglement.chantier', 'Chantier')}
                                                    </Label>
                                                    <Select
                                                        options={chantierOptions}
                                                        value={chantierOptions.find(option => option.value === selectedChantier?.value) || null}
                                                        onChange={(option) => setSelectedChantier(option)}
                                                        placeholder={t('reglement.selectChantier', 'Sélectionner...')}
                                                        isClearable={!isQuickPayment}
                                                        isSearchable={!isQuickPayment}
                                                        className=""
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        styles={compactSelectStyles}
                                                        isDisabled={!canEdit || isQuickPayment}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label for="companyId" className="fw-bold text-secondary small mb-1">
                                                        {t('company.company', 'Entreprise')} *
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        id="companyId"
                                                        name="companyId"
                                                        value={values.companyId || ''}
                                                        onChange={(e) => {
                                                            handleChange(e);
                                                            setSelectedCompanyId(e.target.value ? Number(e.target.value) : null);
                                                            setSelectedDocuments([]);
                                                            setIsCautionPayment(false);
                                                            setDocumentFilters({ query: '', amount: '' });
                                                            setDocumentsInitialized(false);
                                                        }}
                                                        onBlur={handleBlur}
                                                        invalid={touched.companyId && errors.companyId}
                                                        disabled={isCompanyFieldDisabled}
                                                        bsSize="sm"
                                                    >
                                                        <option value="">
                                                            {isDocumentBasedReglement
                                                                ? t('company.companyAutoFromDocuments', 'Entreprise déterminée par les documents')
                                                                : t('company.selectCompany', 'Sélectionner une entreprise')}
                                                        </option>
                                                        {companies?.map((item) => (
                                                            <option key={item.id} value={item.id}>
                                                                {item.name}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                    {touched.companyId && errors.companyId && (
                                                        <div className="invalid-feedback d-block">{errors.companyId}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        {selectedClient && (
                                            <Row className="mt-2 g-2">
                                                <Col>
                                                    <Row className="g-2 align-items-stretch">
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <FaBalanceScale className="mb-1 text-success" size={16} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.balance')}</span>
                                                                    {selectedClient.totalCredit > selectedClient.totalDebit ? (
                                                                        <span className="fw-bold text-success" style={{ fontSize: '0.82rem' }}>+{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                                    ) : selectedClient.totalCredit < selectedClient.totalDebit ? (
                                                                        <span className="fw-bold text-danger" style={{ fontSize: '0.82rem' }}>-{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                                    ) : (
                                                                        <span className="fw-bold text-muted" style={{ fontSize: '0.82rem' }}>{formatPrice(0)}</span>
                                                                    )}
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <FaArrowDown className="mb-1 text-danger" size={16} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.totalDebit')}</span>
                                                                    <span className="fw-bold" style={{ fontSize: '0.82rem' }}>{formatPrice(selectedClient.totalDebit)}</span>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <FaArrowUp className="mb-1 text-primary" size={16} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.totalCredit')}</span>
                                                                    <span className="fw-bold" style={{ fontSize: '0.82rem' }}>{formatPrice(selectedClient.totalCredit)}</span>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Card className="shadow-sm h-100 bg-warning bg-opacity-10 border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <FaCalendarAlt className="me-1 text-warning" size={14} />
                                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.echeances')}</span>
                                                                    </div>
                                                                    <span className="fw-bold text-warning" style={{ fontSize: '0.82rem' }}>{echeances?.length || 0}</span>
                                                                    <Button color="link" size="sm" className="p-0 fw-bold text-warning" style={{ fontSize: '0.65rem' }} onClick={() => setShowEcheanceModal(true)}>
                                                                        {t('client.viewDetails')}
                                                                    </Button>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Card className="shadow-sm h-100 bg-info bg-opacity-10 border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <FaShieldAlt className="me-1 text-info" size={14} />
                                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.cautions')}</span>
                                                                    </div>
                                                                    <span className="fw-bold text-info" style={{ fontSize: '0.82rem' }}>{cautions?.length || 0}</span>
                                                                    <Button color="link" size="sm" className="p-0 fw-bold text-info" style={{ fontSize: '0.65rem' }} onClick={() => setShowCautionModal(true)}>
                                                                        {t('client.viewDetails')}
                                                                    </Button>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                        {selectedClient && paymentPurpose === 'DOCUMENTS' && (
                            <Card id="payment-step-2" className={`payment-form-panel payment-form-left-item payment-form-documents-card mt-3 ${activeStep === 2 ? '' : 'd-none'}`}>
                                <CardHeader className="payment-form-section-header d-flex justify-content-between align-items-center">
                                    <div className="fw-bold">
                                        <FaList className="me-2" />
                                        {t('reglement.documentsARegler')}
                                    </div>
                                    {canEdit && (
                                        <Button
                                            color="primary"
                                            size="sm"
                                            className="d-none"
                                            onClick={() => setShowDocumentModal(true)}
                                            disabled={!selectedClient}
                                        >
                                            {t('reglement.selectionnerDocuments', 'Sélectionner les documents')}
                                        </Button>
                                    )}
                                </CardHeader>
                                <CardBody>
                                    <PaymentHelp>Sélectionnez les documents à payer. Leur solde sera affecté automatiquement et le montant du règlement sera calculé par le système.</PaymentHelp>
                                    <Row className="g-2 mb-3 align-items-end">
                                        <Col md={4}>
                                            <FormGroup className="mb-0">
                                                <Label className="mb-1">{t('common.search', 'Rechercher')}</Label>
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text">
                                                        <FaSearch />
                                                    </span>
                                                    <Input
                                                        value={documentFilters.query}
                                                        placeholder={t('reglement.searchDocument', 'NÂ° document, référence, client')}
                                                        onChange={(e) => setDocumentFilters(prev => ({ ...prev, query: e.target.value }))}
                                                        disabled={!selectedClient || !canEdit}
                                                    />
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup className="mb-0">
                                                <Label className="mb-1">{t('common.amount', 'Montant')}</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    bsSize="sm"
                                                    value={documentFilters.amount}
                                                    placeholder={t('reglement.amountFilterPlaceholder', 'Montant restant min')}
                                                    onChange={(e) => setDocumentFilters(prev => ({ ...prev, amount: e.target.value }))}
                                                    disabled={!selectedClient || !canEdit}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={5}>
                                            <div className="d-flex justify-content-md-end gap-3 small text-muted pt-4">
                                                <span>{filteredAvailableDocuments.length} {t('reglement.documents', 'documents')}</span>
                                                <span>{t('reglement.total')}: {formatPrice(filteredAvailableDocuments.reduce((sum, doc) => sum + getDocumentRemainingAmount(doc), 0), false)} DH</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    {!selectedClient ? (
                                        <div className="text-muted text-center py-3 border rounded mb-3">
                                            {t('reglement.selectClientFirst', 'Sélectionnez un client pour afficher les documents')}
                                        </div>
                                    ) : documentLoading ? (
                                        <div className="text-center py-4 border rounded mb-3">
                                            <Spinner color="primary" size="sm" />
                                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                                        </div>
                                    ) : (
	                                        <div className="payment-document-card-section mb-2">
                                            <div className="payment-document-sortbar">
                                                <span>Trier par</span>
                                                {[
                                                    ['numero', 'Document'],
                                                    ['total', 'Montant'],
                                                    ['remaining', 'Reste à régler'],
                                                ].map(([key, label]) => (
                                                    <Button key={key} type="button" size="sm" color={documentSort.key === key ? 'primary' : 'light'} onClick={() => handleDocumentSort(key)}>
                                                        {label}{documentSort.key === key ? (documentSort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                                                    </Button>
                                                ))}
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    color="success"
                                                    className="ms-auto"
                                                    onClick={selectAllVisibleDocuments}
                                                    disabled={!canEdit || selectableVisibleDocuments.length === 0 || allVisibleDocumentsSelected}
                                                >
                                                    Tout sélectionner
                                                </Button>
                                            </div>
                                            {filteredAvailableDocuments.length === 0 ? (
                                                <div className="payment-document-empty">{t('reglement.noDocumentsFound', 'Aucun document trouvé')}</div>
                                            ) : (
                                                <div className="payment-document-card-grid">
                                                    {sortedAvailableDocuments.map((document) => {
                                                        const montantRestant = getDocumentRemainingAmount(document);
                                                        const montantFactureActif = Number(document.montantFactureActif || 0);
                                                        const isSelected = selectedDocuments.some(doc => doc.id === document.id);
                                                        const isQuickDocument = quickDocuments.some(item => Number(item.id) === Number(document.id));

                                                        return (
                                                            <article key={document.id} className={`payment-document-card ${isSelected ? 'is-selected' : ''} ${isQuickDocument ? 'is-suggested' : ''}`}>
                                                                <header className="payment-document-card-header">
                                                                    <div className="payment-document-select">
                                                                        <Input
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            disabled={!canEdit || montantRestant <= 0}
                                                                            onChange={(event) => toggleDocumentSelection(document, event.target.checked, setFieldValue)}
                                                                            aria-label={`Sélectionner ${document.numero}`}
                                                                        />
                                                                        <div>
                                                                            <div className="payment-document-number">{document.numero}</div>
	                                                                            <div className="payment-document-meta">
	                                                                                {document.paymentDocumentType === 'FACTURE' ? 'FACT' : 'BL'}
	                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        {isQuickDocument && <Badge color="warning">Suggéré</Badge>}
                                                                        {renderDocumentStatusBadge(document)}
                                                                    </div>
                                                                </header>
	                                                                <div className="payment-document-metrics">
	                                                                    <div><span>Date</span><strong>{document.dateOperation || document.dateFacture || '-'}</strong></div>
                                                                    <div><span>Retours / avoirs</span><strong>{formatPrice(getDocumentReturnAmount(document), false)} DH</strong></div>
                                                                    <div><span>Montant net</span><strong>{formatPrice(getDocumentNetAmount(document), false)} DH</strong></div>
                                                                    {document.paymentDocumentType === 'BON_LIVRAISON' && montantFactureActif > 0 && (
                                                                        <div><span>Déjà facturé</span><strong>{formatPrice(montantFactureActif, false)} DH</strong></div>
                                                                    )}
                                                                </div>
                                                                <div className="payment-document-allocation">
                                                                    <div className="payment-document-remaining">
                                                                        <span>Reste avant ce règlement</span>
                                                                        <strong>{formatPrice(montantRestant, false)} DH</strong>
                                                                    </div>
                                                                </div>
                                                            </article>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
	                                    <div className="payment-documents-summary border rounded p-3 bg-light-subtle mb-2">
                                        <div className="d-flex flex-wrap justify-content-between gap-3">
                                            <div>
                                                <div className="small text-muted text-uppercase">
                                                    {t('reglement.documentsSelectionnes', 'Documents sélectionnés')}
                                                </div>
                                                <div className="fw-bold fs-5">{selectedDocuments.length}</div>
                                            </div>
                                            <div className="text-md-end">
                                                <div className="small text-muted text-uppercase">
                                                    {t('reglement.total', 'Total')}
                                                </div>
                                                <div className="fw-bold fs-5 text-primary">
                                                    {formatPrice(calculateTotalAmount(), false)} DH
                                                </div>
                                            </div>
                                        </div>
                                        {selectedDocuments.length === 0 && (
                                            <div className="text-muted small mt-2">
                                                {t('reglement.noDocumentsSelected', 'Aucun document sé©lectionné©')}
                                            </div>
                                        )}
                                    </div>
                                    {false ? (
                                        <div className="text-muted text-center py-3">
                                            {t('reglement.noDocumentsSelected', 'Aucun document sélectionné')}
                                        </div>
                                    ) : (
                                        <div className="table-responsive d-none">
                                            <Table bordered hover size="sm" className="mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t('reglement.numero')}</th>
                                                        <th>{t('reglement.date')}</th>
                                                        <th>{t('reglement.montantTTC')}</th>
                                                        <th>{t('reglement.montantPaye')}</th>
                                                        <th>{t('reglement.montantRestant')}</th>
                                                        {canEdit && <th width="80">{t('common.actions', 'Actions')}</th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedDocuments.map((document) => {
                                                        const montantTotal = document.montantTotalTTC ?? document.montantTTC ?? 0;
                                                        const montantPaye = document.montantRegle ?? document.montantPaye ?? 0;
                                                        const montantRestant = montantTotal - montantPaye;

                                                        return (
                                                            <tr key={document.id}>
                                                                <td className="fw-bold">{document.numero}</td>
                                                                <td>{document.createdAt || document.dateFacture || '-'}</td>
                                                                <td>{formatPrice(montantTotal, false)} DH</td>
                                                                <td>{formatPrice(montantPaye, false)} DH</td>
                                                                <td className="fw-bold text-danger">{formatPrice(montantRestant, false)} DH</td>
                                                                {canEdit && (
                                                                    <td>
                                                                        <Button
                                                                            color="link"
                                                                            size="sm"
                                                                            className="text-danger p-0"
                                                                            onClick={() => removeDocument(document.id, setFieldValue)}
                                                                        >
                                                                            <FaTrash />
                                                                        </Button>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
	                                </CardBody>
	                            </Card>
	                        )}
                            {activeStep === 2 && (
                                <PaymentFinancialSummary
                                    paymentAmount={values.montantRegle}
                                    allocatedAmount={calculateTotalAmount()}
                                    currentPaymentAllocated={calculateCurrentPaymentAllocation(selectedDocuments)}
                                    advanceAmount={calculateAdvanceUsageAmount(selectedDocuments)}
                                    selectedCount={selectedDocuments.length}
                                    partyKind="client"
                                />
                            )}
                        </section>
                        <aside className="payment-side-pane">
                        {selectedClient && (
                            <Alert color={Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) >= calculateTotalAmount() ? 'info' : 'warning'} className={`payment-form-right-item mt-3 mb-3 ${activeStep === 3 ? '' : 'd-none'}`}>
                                <div className="fw-bold mb-1">Situation avant paiement</div>
                                <div className="small">
                                    {selectedDocuments.length} document(s) selectionne(s),
                                    reste a payer {formatPrice(calculateTotalAmount(), false)} DH.
                                    {loadingAdvances ? (
                                        <span className="ms-1">Verification des avances...</span>
                                    ) : totalAvailableAdvances > 0 && (
                                        <span className="text-success ms-1">
                                            Avances disponibles {formatPrice(totalAvailableAdvances, false)} DH,
                                            selectionnees {formatPrice(calculateAdvanceUsageAmount(selectedDocuments), false)} DH.
                                        </span>
                                    )}
                                    Montant saisi {formatPrice(Number(values.montantRegle || 0), false)} DH.
                                    {Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) > calculateTotalAmount() && (
                                        <span className="text-success ms-1">
                                            Le surplus de {formatPrice(Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) - calculateTotalAmount(), false)} DH restera en {isCautionPayment ? 'caution client' : 'avance client'}.
                                        </span>
                                    )}
                                    {Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) < calculateTotalAmount() && (
                                        <span className="text-warning ms-1">
                                            Paiement partiel: {formatPrice(calculateTotalAmount() - Number(values.montantRegle || 0) - calculateAdvanceUsageAmount(selectedDocuments), false)} DH restera impaye.
                                        </span>
                                    )}
                                </div>
                            </Alert>
                        )}
                        {selectedClient && (
                            <Card className={`payment-form-panel payment-form-right-item mb-3 ${activeStep === 3 ? '' : 'd-none'}`}>
                                <CardHeader className="payment-form-section-header py-2">
                                    <h6 className="mb-0">{t('reglement.avancesDisponibles', 'Avances disponibles')}</h6>
                                </CardHeader>
                                <CardBody className="py-2">
                                    <div className="small text-muted mb-2">
                                        {selectedDocuments.length > 0
                                            ? t('reglement.selectAvancesToUse', 'Cochez les avances à utiliser pour ces documents.')
                                            : t('reglement.availableAdvancesForCompany', 'Avances disponibles pour cette entreprise.')}
                                    </div>
                                    {loadingAdvances ? (
                                        <div className="text-center py-2">
                                            <Spinner size="sm" color="primary" />
                                            <span className="ms-2 small text-muted">Chargement des avances...</span>
                                        </div>
                                    ) : !selectedCompanyId ? (
                                        <div className="small text-muted py-2">
                                            {t('company.selectCompany', 'Sélectionner une entreprise')}
                                        </div>
                                    ) : externalAdvances.length === 0 ? (
                                        <div className="small text-muted py-2">
                                            {t('reglement.noAvailableAdvancesForCompany', 'Aucune avance disponible pour cette entreprise.')}
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <Table size="sm" className="mb-0 align-middle">
                                                <tbody>
                                                    {externalAdvances.map((avance) => {
                                                        const checked = selectedAdvanceIds.includes(avance.id);
                                                        return (
                                                            <tr key={avance.id}>
                                                                <td style={{ width: 34 }}>
                                                                    <Input
                                                                        type="checkbox"
                                                                        checked={checked}
                                                                        disabled={!canEdit || selectedDocuments.length === 0}
                                                                        onChange={(event) => {
                                                                            const selected = event.target.checked;
                                                                            setSelectedAdvanceIds(prev => selected
                                                                                ? (prev.includes(avance.id) ? prev : [...prev, avance.id])
                                                                                : prev.filter(id => id !== avance.id));
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <div className="fw-bold">{avance.referenceReglement || `Avance #${avance.id}`}</div>
                                                                    <div className="text-muted small">{avance.dateCreation || '-'} · origine {formatPrice(avance.montantAvance || 0, false)} DH · utilisé {formatPrice(avance.montantUtilise || 0, false)} DH</div>
                                                                </td>
                                                                <td className="text-end fw-bold text-success">
                                                                    {formatPrice(avance.montantDisponible, false)} DH
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        )}
	                        <Card id="payment-step-3" className={`payment-form-panel payment-form-right-item payment-form-details-card ${activeStep === 3 ? '' : 'd-none'}`}>
                            <CardHeader className="payment-form-section-header">
                                <h5 className="mb-0">
                                    <FaEdit className="me-2" />
                                    {t('reglement.detailsReglement')}
                                </h5>
                            </CardHeader>

	                            <CardBody>
	                                <Row className="g-2 payment-form-details-grid">
	                                    <Col md={12}>
	                                        <FormGroup className="payment-amount-control">
	                                            {paymentPurpose === 'DOCUMENTS' && selectedDocuments.length > 0 && totalAvailableAdvances > 0 && (
	                                                <div className={`available-advance-callout ${selectedAdvanceIds.length > 0 ? 'is-applied' : ''}`} role="status">
	                                                    <div>
	                                                        <strong>
	                                                            {selectedAdvanceIds.length > 0 ? 'Avances prises en compte' : 'Des avances sont disponibles'}
	                                                        </strong>
	                                                        <span>
	                                                            {formatPrice(totalAvailableAdvances, false)} DH disponibles · {formatPrice(calculateAdvanceUsageAmount(selectedDocuments), false)} DH appliqués
	                                                        </span>
	                                                    </div>
	                                                    <Button
	                                                        type="button"
	                                                        color={selectedAdvanceIds.length > 0 ? 'outline-success' : 'success'}
	                                                        size="sm"
	                                                        disabled={!canEdit || loadingAdvances}
	                                                        onClick={() => {
	                                                            advancePreferenceInitializedRef.current = true;
	                                                            applyAvailableAdvancesFirst(setFieldValue);
	                                                        }}
	                                                    >
	                                                        {selectedAdvanceIds.length > 0 ? 'Recalculer avec les avances' : 'Utiliser les avances d’abord'}
	                                                    </Button>
	                                                </div>
	                                            )}
	                                            <Label for="montantRegle">
	                                                Nouveau montant à recevoir *
	                                            </Label>
                                            <Field
                                                name="montantRegle"
                                                type="number"
                                                onFocus={(e) => {
                                                    e.target.select();
                                                    e.target.addEventListener('wheel', preventScroll, { passive: false });
                                                }}
                                                onBlur={(e) => {
                                                    e.target.removeEventListener('wheel', preventScroll);
                                                }}

                                                className={`form-control ${errors.montantRegle && touched.montantRegle ? 'is-invalid' : ''}`}
                                                placeholder="0.00"
                                                value={values.montantRegle}
                                                onChange={(e) => {
                                                    if (canEdit) {
                                                        setFieldValue('montantRegle', e.target.value);
                                                        if (paymentPurpose === 'DOCUMENTS') {
                                                            setSelectedDocuments(previous => allocateDocumentsAutomatically(
                                                                previous,
                                                                Number(e.target.value || 0) + calculateSelectedAdvanceCapacity()
                                                            ));
                                                        }
                                                    }
                                                }}
                                                disabled={!canEdit}
                                            />
                                            <ErrorMessage name="montantRegle" component="div" className="invalid-feedback" />
                                            <div className="payment-form-field-hint">
                                                {paymentPurpose === 'DOCUMENTS' && selectedDocuments.length > 0
                                                    ? 'Le système répartit automatiquement ce montant sur les documents sélectionnés. Tout surplus sera conservé comme avance client.'
                                                    : 'Saisissez le montant réellement reçu du client.'}
                                            </div>


                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="dateReglement">
                                                <FaCalendarAlt className="me-1" />
                                                {t('reglement.dateReglement')} *
                                            </Label>
                                            <Field
                                                name="dateReglement"
                                                type="date"
                                                className={`form-control ${errors.dateReglement && touched.dateReglement ? 'is-invalid' : ''}`}
                                                disabled={!canEdit}
                                            />
                                            <ErrorMessage name="dateReglement" component="div" className="invalid-feedback" />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="dateEncaissement">
                                                <FaCalendarAlt className="me-1" />
                                                {t('reglement.dateEncaissement')}
                                            </Label>
                                            <Field
                                                name="dateEncaissement"
                                                type="date"
                                                className="form-control"
                                                disabled={!canEdit}
                                            />
                                        </FormGroup>
                                    </Col>
                                    {selectedDocuments.length === 0 && (
                                        <Col md={12}>
                                            <FormGroup check className="payment-caution-option">
                                                <Input
                                                    id="isCautionPayment"
                                                    type="checkbox"
                                                    checked={isCautionPayment}
                                                    disabled={!canEdit}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setIsCautionPayment(checked);
                                                        setTypeReglement(checked ? 'CAUTION' : 'AVANCE');
                                                        if (checked) {
                                                            setSelectedDocuments([]);
                                                            setDocumentFilters({ query: '', amount: '' });
                                                        }
                                                    }}
                                                />
                                                <Label check for="isCautionPayment">
                                                    {t('reglement.caution', 'Caution')}
                                                </Label>
                                                <div className="payment-form-field-hint">
                                                    Sans document selectionne, le paiement est une avance par defaut. Cochez pour le saisir comme caution.
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    )}
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label for="modeReglement">{t('reglement.modeReglement')} *</Label>
                                            <Field
                                                as="select"
                                                name="modeReglement"
                                                className={`form-control ${errors.modeReglement && touched.modeReglement ? 'is-invalid' : ''}`}
                                                disabled={!canEdit}
                                            >
                                                <option value="">{t('reglement.selectModeReglement')}</option>
                                                {modeReglementOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="modeReglement" component="div" className="invalid-feedback" />
                                        </FormGroup>
                                    </Col>
                                    {(['CHEQUE', 'TRAITE'].includes(values.modeReglement)) && (
                                        <>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label for="numeroCheque">
                                                        {values.modeReglement === 'TRAITE'
                                                            ? t('reglement.numeroTraite', 'Numero traite')
                                                            : t('reglement.numeroCheque')}
                                                    </Label>
                                                    <Field
                                                        name="numeroCheque"
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={values.modeReglement === 'TRAITE'
                                                            ? t('reglement.numeroTraite', 'Numero traite')
                                                            : t('reglement.numeroCheque')}
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label for="banque">{t('reglement.banque')}</Label>
                                                    <Input
                                                        type="select"
                                                        id="banque"
                                                        name="banque"
                                                        value={values.banque}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        invalid={touched.banque && errors.banque}
                                                        disabled={!canEdit}
                                                    >
                                                        <option value="">{t('payment.selectBank', 'Sélectionner une banque')}</option>
                                                        <option value="BMCE">BMCE</option>
                                                        <option value="ATTIJARI">ATTIJARI</option>
                                                        <option value="CIH">CIH</option>
                                                        <option value="CREDIT DU MAROC">CREDIT DU MAROC</option>
                                                        <option value="SG">SG</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label for="dateEcheance">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateEcheance')}
                                                    </Label>
                                                    <Field
                                                        name="dateEcheance"
                                                        type="date"
                                                        className="form-control"
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </>

                                    )}
                                    {(values.modeReglement === 'VIREMENT') && (
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label for="referencePaiement">{t('reglement.referencePaiement')}</Label>
                                                <Field
                                                    name="referencePaiement"
                                                    type="text"
                                                    className="form-control"
                                                    disabled={!canEdit}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>



                                <Row className="mt-3">
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="commentaire">{t('reglement.commentaire')}</Label>
                                            <Field
                                                as="textarea"
                                                name="commentaire"
                                                rows="3"
                                                className="form-control"
                                                placeholder={t('reglement.commentairePlaceholder')}
                                                disabled={!canEdit}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>




                                {/* Allocations Table (Edit Mode) */}
                                {reglement && Array.isArray(reglement.allocations) && reglement.allocations.length > 0 && (
                                    <Card className="mt-3 border-info">
                                        <CardHeader className="bg-info text-white py-2">
                                            <h6 className="mb-0">{t('reglement.allocationsPayments', 'Affectations du réglement')}</h6>
                                        </CardHeader>
                                        <CardBody>
                                            <div className="table-responsive">
                                                <Table bordered hover size="sm">
                                                    <thead className="table-info">
                                                        <tr>
                                                            <th>{t('reglement.documentNumber', 'Document')}</th>
                                                            <th>{t('reglement.montantDocument', 'Montant')}</th>
                                                            <th>{t('reglement.montantAllouex', 'Payé')}</th>
                                                            <th>{t('reglement.montantRestantx', 'Reste')}</th>
                                                            <th>{t('reglement.commentairex', 'Note')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reglement.allocations.map(allocation => (
                                                            <tr key={allocation.id}>
                                                                <td>
                                                                    <div className="fw-semibold">{allocation.numeroDocument}</div>
                                                                    <small className="text-muted">
                                                                        {allocation.sourcePaiement === 'AVANCE' ? 'Avance' : 'Paiement'}
                                                                        {' '}
                                                                        {allocation.referenceSourcePaiement || allocation.refReglement || ''}
                                                                    </small>
                                                                </td>
                                                                <td>{formatPrice(allocation.montantDocument)}</td>
                                                                <td className="fw-bold text-success">{formatPrice(allocation.montantAlloue)}</td>
                                                                <td className="fw-bold text-danger">{formatPrice(allocation.montantRestant)}</td>
                                                                <td>{allocation.commentaire || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <td className="fw-bold text-end">{t('reglement.total', 'Total')}:</td>
                                                            <td className="fw-bold">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantDocument, 0))}</td>
                                                            <td className="fw-bold text-success">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantAlloue, 0))}</td>
                                                            <td className="fw-bold text-danger">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantRestant, 0))}</td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}
                            </CardBody>
                        </Card>
                        {/* Boutons d'action */}
                        {activeStep !== 2 && activeStep !== 4 && <PaymentFinancialSummary paymentAmount={values.montantRegle} allocatedAmount={calculateTotalAmount()} currentPaymentAllocated={calculateCurrentPaymentAllocation(selectedDocuments)} advanceAmount={calculateAdvanceUsageAmount(selectedDocuments)} selectedCount={selectedDocuments.length} partyKind="client" />}
                        <Card id="payment-step-4" className={`payment-form-panel payment-form-right-item payment-form-actionbar mt-1 ${activeStep === 4 ? '' : 'd-none'}`}>
                            <CardBody>
                                <PaymentFinancialSummary paymentAmount={values.montantRegle} allocatedAmount={calculateTotalAmount()} currentPaymentAllocated={calculateCurrentPaymentAllocation(selectedDocuments)} advanceAmount={calculateAdvanceUsageAmount(selectedDocuments)} selectedCount={selectedDocuments.length} partyKind="client" />
                                <div className="payment-review-grid mb-3">
                                    <div><span>Client</span><strong>{selectedClient?.label || '-'}</strong></div>
                                    <div><span>Entreprise</span><strong>{resolveCompanyDisplayName(values.companyId, selectedDocuments)}</strong></div>
                                    <div><span>Date</span><strong>{values.dateReglement || '-'}</strong></div>
                                    <div><span>Mode</span><strong>{modeReglementOptions.find(option => option.value === values.modeReglement)?.label || values.modeReglement || '-'}</strong></div>
                                    <div><span>Montant reçu</span><strong>{formatPrice(values.montantRegle || 0, false)} DH</strong></div>
                                    <div><span>Référence</span><strong>{values.referencePaiement || values.numeroCheque || '-'}</strong></div>
                                </div>
                                <PaymentHelp color="warning">Après confirmation, les soldes et statuts des documents sélectionnés seront recalculés.</PaymentHelp>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        {selectedDocuments.length > 0 && (
                                            <small className="text-muted">
                                                {selectedDocuments.length} {t('reglement.documentsSelectionnes')} -
                                                {t('reglement.total')}: <strong>{formatPrice(calculateTotalAmount())}</strong>
                                            </small>
                                        )}
                                    </div>
                                    {
                                        canEdit && <div className="d-flex gap-2">

                                            <Button
                                                type="submit"
                                                color="primary"
                                                className="payment-primary-action"
                                                disabled={
                                                    isSubmitting ||
                                                    (!selectedClient) ||
                                                    ((!values.montantRegle || values.montantRegle <= 0) && calculateAdvanceUsageAmount(selectedDocuments) <= 0)
                                                }
                                            >
                                                {isSubmitting && <Spinner size="sm" className="me-2" />}
                                                <i className={`fas ${reglement ? 'fa-save' : 'fa-plus'} me-1`}></i>
                                                {isSubmitting ? 'Enregistrement et mise à jour des soldes…' : reglement ? 'Enregistrer les modifications du règlement' : paymentPurpose === 'ADVANCE' ? 'Enregistrer l’avance' : 'Confirmer le règlement client'}
                                            </Button>


                                        </div>}
                                </div>
                            </CardBody>
                        </Card>
                        </aside>
                        </div>
                    </Form>
                )}
            </Formik>

            {/* Modal de sélection des documents */}
            <DocumentSelectionModal
                isOpen={showDocumentModal}
                toggle={() => setShowDocumentModal(false)}
                typeDocument={typeReglement}
                documents={availableDocuments}
                loading={documentLoading}
                selectedDocuments={selectedDocuments}
                onDocumentsSelected={(documents) => handleDocumentsSelected(documents)}
            />
            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>
            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default ReglementForm;


